<script setup lang="ts">
import { onClickOutside } from "@vueuse/core";
const { t } = useI18n();

const {
  name = "",
  placeholder = "",
  buttonText = "select",
  maxRooms = 10,
  maxGuests = 2,
  infoMessage = "",
} = defineProps<{
  name?: string;
  placeholder?: string;
  buttonText?: string;
  maxRooms?: number;
  maxGuests?: number;
  infoMessage?: string;
}>();

const rooms = defineModel<number>("rooms", {
  default: 1,
});
const guestsPerRoom = defineModel<number>("guestsPerRoom", {
  default: 1,
});

const showOptions = ref(false);

const countFormatted = computed(
  () =>
    `${t("website.booking.panel_rooms_v2", { count: rooms.value })} - ${t("website.booking.panel_guests_v2", { count: guestsPerRoom.value })}`,
);

const showInputOptions = () => {
  showOptions.value = true;
};

const closeOptions = () => {
  showOptions.value = false;
};

const personRoomsSelect = useTemplateRef("person-rooms-select");
onClickOutside(personRoomsSelect, () => closeOptions());
</script>

<template>
  <div class="citizem-person-rooms-count-wrap" ref="person-rooms-select">
    <input type="hidden" :name="name + '_guests'" :value="guestsPerRoom" />
    <input type="hidden" :name="name + '_rooms'" :value="rooms" />

    <Input
      @focus="showInputOptions()"
      @keydown.enter.prevent="showInputOptions()"
      @keydown.space.prevent="showInputOptions()"
      v-bind="$attrs"
      v-model="countFormatted"
      :placeholder="placeholder"
      :id="`inp_pgc_${name}`"
      class="input-people-count"
      readonly
      fullwidth
      aria-haspopup="dialog"
      :aria-controls="`modal-pgc-${name}`"
      :aria-label="placeholder"
      tabindex="0"
      :tabIndexValue="-1"
    >
      <template v-slot:after-icon>
        <IconPeople />
      </template>
    </Input>

    <ModalMobile
      @close="closeOptions"
      :only-mobile="true"
      :full-height="true"
      :visible="showOptions"
      :id="`modal-pgc-${name}`"
      role="dialog"
      aria-modal="true"
    >
      <template v-slot:header>
        <H level="3" size="5" class="mt24 mb16">{{ placeholder }}</H>
      </template>
      <template v-slot:default>
        <FormLabelWithDescription
          :label="$t('website.booking.panel_label_rooms')"
          :info="$t('website.booking.panel_roomsInfo')"
          class="mb16"
        >
          <InputNumber
            name="rooms"
            :min="1"
            :max="maxRooms"
            :step="1"
            v-model="rooms"
          />
        </FormLabelWithDescription>
        <FormLabelWithDescription
          :label="$t('website.booking.panel_label_guests')"
          :info="$t('website.booking.panel_guestsInfo')"
        >
          <InputNumber
            name="guests"
            :min="1"
            :max="maxGuests"
            :step="1"
            v-model="guestsPerRoom"
          />
        </FormLabelWithDescription>
      </template>
      <template v-slot:footer>
        <div class="modal-shadow-footer">
          <MessageInfo class="mb16" type="info" v-if="infoMessage">
            <span v-html="infoMessage"></span>
          </MessageInfo>
          <div class="modal-selected-value">
            {{ countFormatted }}
          </div>
          <Button fullwidth @click.prevent="closeOptions">
            {{ buttonText }}
          </Button>
        </div>
      </template>
    </ModalMobile>

    <SurfacePaper
      class="person-rooms-options-wrap"
      :desktop-only="true"
      :less-padding="true"
      v-if="showOptions"
      :id="`desktop-modal-pgc-${name}`"
      role="dialog"
      aria-modal="true"
    >
      <MessageInfo class="mb16" type="info" v-if="infoMessage">
        <span v-html="infoMessage"></span>
      </MessageInfo>
      <div class="person-rooms-options modal-drop-down-options">
        <FormLabelWithDescription
          :label="$t('website.booking.panel_label_rooms')"
          :info="$t('website.booking.panel_roomsInfo')"
          class="mb16"
        >
          <InputNumber
            name="rooms"
            :min="1"
            :max="maxRooms"
            :step="1"
            v-model="rooms"
          />
        </FormLabelWithDescription>
        <FormLabelWithDescription
          :label="$t('website.booking.panel_label_guests')"
          :info="$t('website.booking.panel_guestsInfo')"
        >
          <InputNumber
            name="guests"
            :min="1"
            :max="maxGuests"
            :step="1"
            v-model="guestsPerRoom"
          />
        </FormLabelWithDescription>
      </div>
    </SurfacePaper>
  </div>
</template>

<style lang="scss">
.person-rooms-options-wrap {
  position: absolute;
  top: calc(100% + var(--space-16));
  left: 0;
  z-index: 9;
  width: max-content;

  .info-message {
    font-size: var(--font-size-caption-small);
    line-height: var(--line-height-caption-small);
  }
}

.modal-drop-down-options {
  &.person-rooms-options {
    padding: 0;
    overflow: visible;
    border: none;
    border-radius: 0;
  }
}

.input-people-count {
  .inp {
    padding-right: 0;
    cursor: default;
  }
}

.citizem-person-rooms-count-wrap {
  position: relative;
  max-width: 340px;
}
</style>
